import React, { useState } from 'react';
import useSWR from 'swr';
import styled from '@emotion/styled';
import { AutoNav } from './AutoNav';
import { Link } from 'components/Link/Link';
import { Colors, Spinner } from '@zapier/design-system';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '@zapier/universal-layout';
import fetchNavigationItems from './fetchNavigationItems';
import { navigationItems as fallbackNavItems } from 'constants/blogNavigation';
import { NavItemType } from './types';
import { mediaQueries } from '../mediaQueries';

const getDefaultSelectedItem = (navItems: NavItemType[] | undefined) => {
  const currentPath = window.location.pathname.replace(/\/$/, '');
  return navItems
    ? navItems.find(
        (navItem) =>
          navItem.href === currentPath ||
          navItem.submenu?.some(
            (subMenuItem) => subMenuItem.href === currentPath,
          ),
      )
    : fallbackNavItems.find(
        (fbNavItem) =>
          fbNavItem.href === currentPath ||
          fbNavItem.submenu?.some(
            (subMenuItem) => subMenuItem.href === currentPath,
          ),
      );
};

const getSelectedItemHref = (item: NavItemType | undefined) => {
  return (item?.href || window.location.pathname).replace(/\/$/, '');
};

export function SecondaryNavigation() {
  const { data: navItems, error } = useSWR('navItems', fetchNavigationItems, {
    revalidateOnFocus: false,
  });
  const [currentItem, setCurrentItem] = useState<NavItemType>();

  React.useEffect(() => {
    setCurrentItem(getDefaultSelectedItem(navItems));
  }, [navItems]);

  const navJsx = (items: NavItemType[]) => (
    <Outer data-testid="secondary-navigation">
      <NavWrapper>
        <AutoNav
          items={items}
          onChooseItem={(item) => {
            setCurrentItem(item);
          }}
          selectedItemHref={getSelectedItemHref(currentItem)}
          isItemSelected={(item, selectedItemHref) => {
            return (
              (item.href && item.href === selectedItemHref) ||
              (!item.href &&
                item.submenu?.some(
                  (subMenuItem) => subMenuItem.href === selectedItemHref,
                ))
            );
          }}
          size="large"
          underlineSelectedItem={false}
          linkComponent={Link}
          shouldRenderTrack={false}
        />
      </NavWrapper>
    </Outer>
  );
  if (navItems) return navJsx(navItems);

  if (error) {
    console.error(error.message);

    return navJsx(fallbackNavItems);
  }

  return (
    <SpinnerWrapper>
      <Spinner />
    </SpinnerWrapper>
  );
}

const Outer = styled.div`
  background: ${Colors.neutral100};
  border-bottom: 1px solid ${Colors.neutral300};
  position: sticky;
  top: ${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT}px;
  z-index: 50;

  @media (min-width: 450px) {
    top: ${UNIVERSAL_TOPBAR_HEIGHT}px;
  }
`;

const NavWrapper = styled.div`
  max-width: 1175px;
  margin: 0 20px 0 10px;

  ${mediaQueries.desktop} {
    margin: 0 auto;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  padding: 15px;
  justify-content: center;
  border-bottom: 1px solid ${Colors.neutral300};
`;
